import { Button, Form, Input, Checkbox, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/userSlice";
import { apiRequest } from "../../utils/apiClient";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import AuthLogo from "../../components/auth/AuthLogo";
import { HeatMapOutlined } from '@ant-design/icons';

const Login = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogin = async (formValues) => {
    setLoading(true);

    try {
      const response = await apiRequest(
        "/api/auth/login",
        {
          method: "POST",
          body: JSON.stringify(formValues),
        },
        true
      );

      const { user } = response;

      dispatch(
        setUser({
          id: user.id ?? user._id,
          username: user.username,
          email: user.email,
          role: user.role ?? "admin",
          invoiceType: user.invoiceType,
          cnic: user.cnic,
          province: user.province,
          address: user.address,
          businessName: user.businessName,
          businessPhone: user.businessPhone,
          ntn: user.ntn,
          salesTaxNumber: user.salesTaxNumber,
        })
      );

      message.success(t("loginSuccess"));
      navigate("/");
    } catch (error) {
      const errorMessage =
        error?.message ??
        error?.data?.message ??
        error?.data?.error ??
        t("loginFailed");
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="select-none min-h-screen bg-[#f4f8ff] px-4 py-6 sm:px-8 lg:px-12 lg:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-6xl items-center gap-8 xl:gap-16">
        <div className="w-full lg:w-[42%] xl:w-[38%]">
          <div className="mx-auto max-w-md">
            <h1 className="mt-10 mb-8 text-3xl font-semibold tracking-tight text-gray-900 leading-tight">
              Sell Smarter.{" "}
              <span className="text-[#1677ff]">Grow Effortlessly.</span>
            </h1>

            <Form
              layout="vertical"
              onFinish={handleLogin}
              initialValues={{ remember: false }}
              className="space-y-4"
            >
              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("email")}</span>}
                name="email"
                rules={[
                  {
                    required: true,
                    message: t("emailRequired"),
                  },
                ]}
                className="mb-4"
              >
                <Input
                  size="large"
                  prefix={<MailOutlined className="text-[#808080] mr-1" />}
                  placeholder="someone@example.com"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-sm font-medium text-[#2f2f2f]">{t("password")}</span>}
                name="password"
                rules={[
                  {
                    required: true,
                    message: t("passwordRequired"),
                  },
                ]}
                className="mb-3"
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined className="text-[#808080] mr-1" />}
                  placeholder="•••••••••••••••••"
                  className="!h-11 !rounded-lg !border-0 !bg-[#EEE] !shadow-none hover:!bg-[#e4e4e5] focus-within:!bg-[#e4e4e5]"
                />
              </Form.Item>

              <Form.Item name="remember" valuePropName="checked" className="mb-4">
                <Checkbox className="text-sm text-[#303030]">{t("rememberMe")}</Checkbox>
              </Form.Item>

              <Form.Item className="mb-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="font-bold !h-11 !w-full !rounded-lg !bg-[#1677ff] !text-white hover:!bg-[#0e66e1]"
                  loading={loading}
                >
                  {t("Securely sign in via Email")}
                </Button>
              </Form.Item>
            </Form>

            <div className="mb-6 flex flex-col gap-1 text-sm text-[#555] mt-5">
              <span>
                {t("noAccountYet")}
                <Link to="/register" className="ml-1 font-medium text-[#1677ff] hover:text-[#0e66e1]">
                  {t("signUpNow")}
                </Link>
              </span>
              <Link to="/forgot-password" className="w-fit text-[#1677ff] hover:text-[#0e66e1]">
                {t("forgotPassword")}
              </Link>
            </div>

            <a
              href="https://www.vareonofficial.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-[250px]"
            >
              <div className="py-3 rounded-xl bg-[#eee] flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-md">
                {/* <img src="/logo5.png" alt="VAREON" className="w-8 h-8" /> */}
                <HeatMapOutlined style={{ fontSize: 25 }} />
                <p className="ml-1 text-sm font-medium text-gray-700">
                  Powered by <span className="text-black">VAREON</span>
                </p>
              </div>
            </a>
          </div>
        </div>

        <div className="relative hidden h-[88vh] min-h-[680px] flex-1 overflow-hidden rounded-[36px] bg-gradient-to-br from-[#1677ff] via-[#2c7df0] to-[#0b52c4] p-10 text-white lg:block xl:p-12">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.22),transparent_35%),radial-gradient(circle_at_12%_18%,rgba(255,255,255,0.15),transparent_30%)]" />

          <div className="relative flex h-full flex-col justify-between">
            <div className="max-w-md pt-4">
              <p className="mb-3 text-base text-[#dfeeff]">VAREON - Point of Sale</p>
              <h2 className="mb-4 text-4xl font-semibold leading-tight text-white">
                Pakistan&apos;s No. 1 POS and Accounting Software
              </h2>
              <p className="mb-5 text-sm leading-7 text-[#eaf3ff]">
                Manage billing, inventory, expenses, receivables, and business reports in one
                simple platform. Built for Pakistani businesses that need speed, accuracy, and
                full control.
              </p>
              <p className="text-base text-[#f2f7ff]">
                Trusted by retailers, restaurants, pharmacies, and wholesalers across Pakistan.
              </p>
            </div>

            <div className="relative max-w-[520px] rounded-[28px] bg-white/20 px-8 py-9 backdrop-blur-sm">
              <h3 className="mb-4 text-4xl font-semibold leading-tight text-white">
                Everything Your Business Needs, In One Place
              </h3>
              <p className="max-w-md text-sm leading-7 text-[#eaf3ff]">
                Fast checkout, smart stock control, supplier and customer ledgers, and real-time
                sales insights to help you grow with confidence every day.
              </p>
              <div className="mt-6 inline-flex rounded-full bg-white px-4 py-2 text-sm font-medium text-[#0f4dc5]">
                Complete POS + Accounts + Reports
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;